import type { RendererMap } from 'types/aspect';
import type { Schema, SchemaProperty, SchemaSection } from 'types/schema';
import { DEFAULT_SECTION_ORDER } from '@center-platform/spheres/identity';
import type { SphereSectionSlot, SphereSectionSlotContext } from '../components/organisms/SphereEditor/sphereSectionSlots';

/** Key of the implicit group that holds every property without a (known) section. */
export const DEFAULT_SECTION_KEY = '__default';

/** Properties the sphere header renders itself; no section group ever holds them. */
export const HEADER_RENDERED_PROPERTY_NAMES: readonly string[] = ['urgency', 'type', 'name', 'description', 'is_private'];

export interface SectionLayoutGroup {
  key: string;
  /** null = implicit Details group (order DEFAULT_SECTION_ORDER). */
  section: SchemaSection | null;
  /** Grid rows, slot-owned properties removed. */
  properties: SchemaProperty[];
  /** Every visible property resolved to this section, slot-owned ones included. */
  allProperties: SchemaProperty[];
  /** Present only when the slot's appliesTo() is true. */
  slot?: SphereSectionSlot;
  /** The context the slot was resolved with; its `properties` include the slot-owned ones. */
  slotContext?: SphereSectionSlotContext;
}

export interface SectionLayout {
  inline: SectionLayoutGroup[];
  docked: SectionLayoutGroup[];
  /** Legacy `dock: true` properties that are not inside a docked section. */
  dockedProperties: SchemaProperty[];
  /** True when inline is exactly [__default] and docked is empty. */
  onlyDefault: boolean;
}

export interface ResolveSectionLayoutArgs {
  sections: SchemaSection[] | undefined;
  /** The viewer-visible property set, in schema order. */
  properties: SchemaProperty[];
  slots: Readonly<Record<string, SphereSectionSlot>>;
  /** The sphere-level context; `mode`/`editable` are narrowed per section (sphereSectionSlots.ts). */
  slotContext: Omit<SphereSectionSlotContext, 'section' | 'properties' | 'writeBlockedProps'>;
  /** Every property the viewer cannot write (computeWriteBlockedProps), the same set the rows lock. */
  writeBlockedProps: ReadonlySet<string>;
  /** Whole-aspect renderer refs; an aspect listed here keeps all its properties in one group. */
  aspectComponents?: Record<string, RendererMap>;
}

const IMPLICIT_DEFAULT_SECTION: SchemaSection = {
  system_name: DEFAULT_SECTION_KEY,
  label: 'Details',
  label_key: 'sphere.sections.details',
  order: DEFAULT_SECTION_ORDER,
};

function orderOf(section: SchemaSection | null): number {
  return section?.order ?? DEFAULT_SECTION_ORDER;
}

/**
 * The order section groups render in: ascending `order`, the implicit Details group (`section`
 * null) at DEFAULT_SECTION_ORDER, ties broken by key.
 */
export function compareSectionGroups(
  a: { key: string; section: SchemaSection | null },
  b: { key: string; section: SchemaSection | null }
): number {
  return orderOf(a.section) - orderOf(b.section) || a.key.localeCompare(b.key);
}

/**
 * Splits the visible properties into ordered section groups. Slot failures are logged with the
 * section key only (never labels or values) and treated as "not applicable".
 *
 * Each group's slot context is the section-level answer: a section whose every property is
 * write-blocked is `view`, a sphere-level `view` is never upgraded, and a section with no properties
 * keeps the sphere-level answer. `writeBlockedProps` is narrowed to the section's own properties.
 */
export function resolveSectionLayout({
  sections,
  properties,
  slots,
  slotContext,
  writeBlockedProps,
  aspectComponents,
}: ResolveSectionLayoutArgs): SectionLayout {
  const sectionByName = new Map<string, SchemaSection>();
  for (const section of sections ?? []) sectionByName.set(section.system_name, section);

  const keyOf = (prop: SchemaProperty): string =>
    prop.section && sectionByName.has(prop.section) ? prop.section : DEFAULT_SECTION_KEY;

  // A whole-aspect renderer mounts once, so all of its properties follow the first one.
  const aspectKey = new Map<string, string>();
  for (const prop of properties) {
    const aspect = prop.aspect_system_name;
    if (aspect && aspectComponents?.[aspect] && !aspectKey.has(aspect)) aspectKey.set(aspect, keyOf(prop));
  }

  const propsByKey = new Map<string, SchemaProperty[]>();
  for (const prop of properties) {
    const key = (prop.aspect_system_name && aspectKey.get(prop.aspect_system_name)) || keyOf(prop);
    propsByKey.set(key, [...(propsByKey.get(key) ?? []), prop]);
  }

  // Slots see only the viewer-visible property set, never the full schema (sphereSectionSlots.ts).
  const visibleSchemaType = { ...slotContext.schemaType, properties };
  const sphereEditable = slotContext.editable && slotContext.mode === 'edit';

  const keys = new Set<string>([DEFAULT_SECTION_KEY, ...sectionByName.keys()]);
  const groups: SectionLayoutGroup[] = [];
  for (const key of keys) {
    const section = key === DEFAULT_SECTION_KEY ? null : (sectionByName.get(key) ?? null);
    const groupProps = propsByKey.get(key) ?? [];
    const slot = slots[key];
    // Over every property of the section, slot-owned ones included, before the slot is consulted.
    const blockedInGroup = new Set(groupProps.filter((prop) => writeBlockedProps.has(prop.name)).map((prop) => prop.name));
    const allBlocked = groupProps.length > 0 && groupProps.every((prop) => blockedInGroup.has(prop.name));
    const editable = sphereEditable && !allBlocked;
    const ctx: SphereSectionSlotContext = {
      ...slotContext,
      schemaType: visibleSchemaType,
      section: section ?? IMPLICIT_DEFAULT_SECTION,
      properties: groupProps,
      mode: editable ? 'edit' : 'view',
      editable,
      writeBlockedProps: blockedInGroup,
    };

    let activeSlot: SphereSectionSlot | undefined;
    if (slot) {
      try {
        if (slot.appliesTo(ctx)) activeSlot = slot;
      } catch {
        console.error(`[resolveSectionLayout] slot appliesTo threw for section "${key}"`);
      }
    }

    let rows = groupProps;
    if (activeSlot?.ownsProperty) {
      const owns = activeSlot.ownsProperty.bind(activeSlot);
      try {
        rows = groupProps.filter((prop) => !owns(prop, ctx));
      } catch {
        console.error(`[resolveSectionLayout] slot ownsProperty threw for section "${key}"`);
      }
    }

    if (rows.length === 0 && !activeSlot) continue;
    groups.push({ key, section, properties: rows, allProperties: groupProps, ...(activeSlot ? { slot: activeSlot, slotContext: ctx } : {}) });
  }

  groups.sort(compareSectionGroups);

  const inline = groups.filter((g) => !g.section?.dock);
  const docked = groups.filter((g) => g.section?.dock === true);
  const dockedKeys = new Set(docked.map((g) => g.key));
  const dockedProperties = properties.filter(
    (prop) => prop.dock === true && !dockedKeys.has((prop.aspect_system_name && aspectKey.get(prop.aspect_system_name)) || keyOf(prop))
  );

  return {
    inline,
    docked,
    dockedProperties,
    onlyDefault: inline.length === 1 && inline[0].key === DEFAULT_SECTION_KEY && docked.length === 0,
  };
}

/** The section an aspect ref places its properties in, or null when it is unsectioned. */
export function resolveAspectSection(schema: Schema, aspectSystemName: string): string | null {
  const ref = schema.aspects?.find((aspect) => aspect.systemName === aspectSystemName);
  return ref?.section ?? null;
}
